"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { EmailGate } from "@/components/shared/EmailGate";
import { ShareBar } from "@/components/shared/ShareBar";
import type { CalculatorDefinition } from "@/lib/calculator-types";

interface CalculatorFormProps {
  calc: CalculatorDefinition;
}

export function CalculatorForm({ calc }: CalculatorFormProps) {
  const initialValues = Object.fromEntries(
    calc.inputs.map((i) => [i.id, i.defaultValue ?? ""]),
  );
  const [values, setValues] = useState<Record<string, any>>(initialValues);

  const result = useMemo(() => {
    try {
      return calc.compute(values);
    } catch {
      return null;
    }
  }, [values, calc]);

  return (
    <div>
      {/* FORM */}
      <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 mb-5">Dine verdier</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {calc.inputs.map((input) => (
            <div key={input.id}>
              <label className="text-xs font-medium text-stone-600 block mb-1.5">
                {input.label}
                {input.required === false && (
                  <span className="ml-1 text-stone-400">(valgfritt)</span>
                )}
              </label>
              {input.type === "number" && (
                <div className="relative">
                  <input
                    type="number"
                    value={values[input.id] ?? ""}
                    min={input.min}
                    max={input.max}
                    step={input.step ?? 1}
                    onChange={(e) =>
                      setValues((v) => ({ ...v, [input.id]: e.target.value === "" ? "" : Number(e.target.value) }))
                    }
                    className="w-full px-4 py-3 border border-stone-200 rounded-xl text-base focus:outline-none focus:border-stone-900 focus:ring-2 focus:ring-stone-900/10"
                  />
                  {input.unit && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-stone-400">
                      {input.unit}
                    </span>
                  )}
                </div>
              )}
              {input.type === "radio" && input.options && (
                <div className="grid grid-cols-2 gap-2">
                  {input.options.map((opt) => {
                    const selected = values[input.id] === opt.value;
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => setValues((v) => ({ ...v, [input.id]: opt.value }))}
                        className={
                          "border-2 rounded-xl py-3 text-sm transition-colors " +
                          (selected
                            ? "border-stone-900 bg-stone-50 font-medium"
                            : "border-stone-200 hover:border-stone-400 text-stone-700")
                        }
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {input.type === "select" && input.options && (
                <select
                  value={values[input.id] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [input.id]: e.target.value }))}
                  className="w-full px-4 py-3 border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-stone-900 bg-white"
                >
                  {input.options.map((opt) => (
                    <option key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
              {input.helpText && <p className="text-[11px] text-stone-500 mt-1">{input.helpText}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* MAIN RESULT */}
      {result && result.primary && (
        <div className="bg-gradient-to-br from-stone-900 to-stone-800 text-white rounded-3xl p-8 md:p-10 mb-6">
          <p className="text-xs uppercase tracking-wider text-stone-400 mb-3">{result.primary.label}</p>
          <div className="flex items-baseline gap-3 mb-2">
            <span className="text-7xl md:text-8xl font-light tracking-tight">{result.primary.value}</span>
            {result.primary.unit && <span className="text-xl text-stone-300">{result.primary.unit}</span>}
          </div>
          {result.primary.context && (
            <p className="text-sm text-stone-300 leading-relaxed">{result.primary.context}</p>
          )}
        </div>
      )}

      {/* DETAIL ROWS (e.g. trening-soner) */}
      {result?.details && result.details.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 mb-6">
          <h2 className="text-base font-medium text-stone-900 mb-5">{result.detailsTitle || "Detaljer"}</h2>
          <div className="space-y-3">
            {result.details.map((d, i) => (
              <div key={i} className="flex items-center gap-4 p-4 border border-stone-100 rounded-2xl">
                {d.color && <div className="w-1 h-12 rounded-full" style={{ background: d.color }}></div>}
                <div className="flex-1">
                  <div className="flex items-baseline justify-between mb-0.5">
                    <span className="font-semibold text-stone-900">{d.label}</span>
                    {d.subtext && <span className="text-xs text-stone-500">{d.subtext}</span>}
                  </div>
                  {d.description && <p className="text-xs text-stone-500">{d.description}</p>}
                </div>
                {d.value && (
                  <div className="text-right whitespace-nowrap">
                    <div className="text-base font-bold text-stone-900 tabular-nums">{d.value}</div>
                    {d.valueUnit && <div className="text-[10px] text-stone-500">{d.valueUnit}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* EXPLANATION */}
      {calc.explanation && (
        <details className="bg-white border border-stone-200 rounded-2xl p-5 mb-6">
          <summary className="cursor-pointer font-medium text-stone-900">{calc.explanation.title}</summary>
          <div className="text-sm text-stone-600 leading-relaxed mt-4 space-y-3 whitespace-pre-line">
            {calc.explanation.body}
          </div>
        </details>
      )}

      {/* RELATED LINKS */}
      {calc.related && calc.related.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6">
          <h3 className="font-medium text-stone-900 mb-3">Relatert på CoachGuiden</h3>
          <ul className="space-y-2">
            {calc.related.map((r) => (
              <li key={r.href}>
                <Link href={r.href} className="text-sm text-stone-600 underline underline-offset-2 hover:text-stone-900">
                  {r.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* EMAIL GATE */}
      {calc.emailGate && (
        <EmailGate
          toolSlug={calc.slug}
          toolType="calculator"
          variant="light"
          headline={calc.emailGate.headline}
          description={calc.emailGate.description}
          buttonText={calc.emailGate.buttonText}
          resultData={{
            metadata: {
              kind: "calculator",
              title: calc.title,
              primary: result?.primary ?? null,
              detailsTitle: result?.detailsTitle ?? null,
              details: result?.details ?? null,
              ctaText: calc.cta?.buttonText ?? null,
              ctaHref: calc.cta?.href ?? null,
            },
          }}
        />
      )}

      {/* CTA */}
      {calc.cta && (
        <div className="mt-8 bg-stone-900 text-white rounded-3xl p-7 text-center">
          <h3 className="text-2xl font-light tracking-tight mb-3">{calc.cta.headline}</h3>
          <p className="text-stone-300 text-sm mb-5">{calc.cta.description}</p>
          <Link
            href={calc.cta.href}
            className="inline-block bg-white text-stone-900 px-7 py-3 rounded-full font-medium hover:bg-stone-100 transition-colors"
          >
            {calc.cta.buttonText} →
          </Link>
        </div>
      )}

      <ShareBar label="Del dette verktøyet" />
    </div>
  );
}
